import { DevFeedbackProps } from "../types/devFeedback";

export type ResolvedFeedbackPosition = {
  top: number;
  left: number;
  /** true when we fell back to the captured coordinates instead of a live element match */
  approximate: boolean;
};

/**
 * Anchors a feedback record back onto the live page. Prefers a live
 * `document.querySelector(element_selector)` match (adapts to reflow/scroll
 * automatically); falls back to the click position captured at report time
 * (element_selector can go stale if the DOM structure around it changed).
 * Returns page-absolute coordinates (document top/left, not viewport), so an
 * absolutely-positioned marker scrolls naturally with the page content.
 */
export const resolveFeedbackPosition = (
  feedback: DevFeedbackProps
): ResolvedFeedbackPosition | null => {
  if (feedback.element_selector) {
    try {
      const el = document.querySelector(feedback.element_selector);
      if (el) {
        const rect = el.getBoundingClientRect();
        return {
          top: Math.round(rect.top + window.scrollY),
          left: Math.round(rect.left + window.scrollX),
          approximate: false,
        };
      }
    } catch {
      // element_selector may contain syntax the live DOM can no longer
      // parse as a valid selector (e.g. a class starting with a digit) — fall through
    }
  }

  const meta = feedback.meta || {};
  if (typeof meta.clickX === "number" && typeof meta.clickY === "number") {
    const scrollX = typeof meta.scrollX === "number" ? meta.scrollX : 0;
    const scrollY = typeof meta.scrollY === "number" ? meta.scrollY : 0;
    return {
      top: Math.round(meta.clickY + scrollY),
      left: Math.round(meta.clickX + scrollX),
      approximate: true,
    };
  }

  if (meta.elementRect && typeof meta.elementRect.top === "number") {
    const scrollX = typeof meta.scrollX === "number" ? meta.scrollX : 0;
    const scrollY = typeof meta.scrollY === "number" ? meta.scrollY : 0;
    return {
      top: Math.round(meta.elementRect.top + scrollY),
      left: Math.round(meta.elementRect.left + scrollX),
      approximate: true,
    };
  }

  return null;
};
